import countriesData from '@/assets/json/countries.json';

// Country data interface
export interface Country {
    name: string;
    code: string;
    dial_code: string;
}

// Common country name variations and their standard names
const COUNTRY_NAME_VARIATIONS: Record<string, string> = {
    // United States variations
    'united states of america': 'United States',
    'usa': 'United States',
    'america': 'United States',
    'us': 'United States',
    
    // United Kingdom variations
    'uk': 'United Kingdom',
    'great britain': 'United Kingdom',
    'britain': 'United Kingdom',
    'england': 'United Kingdom',
    'scotland': 'United Kingdom',
    'wales': 'United Kingdom',
    'northern ireland': 'United Kingdom',
    
    // Other common variations
    'russia': 'Russian Federation',
    'south korea': 'Korea, Republic of',
    'north korea': 'Korea, Democratic People\'s Republic of',
    'venezuela': 'Venezuela, Bolivarian Republic of',
    'bolivia': 'Bolivia, Plurinational State of',
    'iran': 'Iran, Islamic Republic of',
    'syria': 'Syrian Arab Republic',
    'vietnam': 'Viet Nam',
    'macedonia': 'North Macedonia',
    'czech republic': 'Czechia',
    'ivory coast': 'Côte d\'Ivoire',
    'cape verde': 'Cabo Verde',
    'east timor': 'Timor-Leste',
    'swaziland': 'Eswatini',
    'burma': 'Myanmar',
    'holland': 'Netherlands',
    'congo': 'Congo',
};

// Reverse mapping from standard names to variations
const REVERSE_VARIATIONS: Record<string, string[]> = {};

// Build reverse mapping
Object.entries(COUNTRY_NAME_VARIATIONS).forEach(([variation, standardName]) => {
    if (!REVERSE_VARIATIONS[standardName]) {
        REVERSE_VARIATIONS[standardName] = [];
    }
    REVERSE_VARIATIONS[standardName].push(variation);
});

/**
 * Normalize a country name for comparison
 */
function normalizeCountryName(name: string): string {
    return name.toLowerCase().trim()
        .replace(/[.,]/g, '') // Remove commas and periods
        .replace(/\s+/g, ' '); // Normalize whitespace
}

/**
 * Get country by code (e.g., "US" -> "United States")
 */
export function getCountryNameByCode(code: string): string | null {
    const normalizedCode = code.toUpperCase().trim();
    const country = countriesData.find(c => c.code === normalizedCode);
    return country ? country.name : null;
}

/**
 * Get country code by name (e.g., "United States" -> "US")
 */
export function getCountryCodeByName(name: string): string | null {
    const normalizedName = normalizeCountryName(name);
    
    // First check direct match
    const directMatch = countriesData.find(c => 
        normalizeCountryName(c.name) === normalizedName
    );
    if (directMatch) return directMatch.code;
    
    // Check variations
    const standardName = COUNTRY_NAME_VARIATIONS[normalizedName];
    if (standardName) {
        const country = countriesData.find(c => c.name === standardName);
        return country ? country.code : null;
    }
    
    // Check partial matches (for cases like "United States of America")
    const partialMatch = countriesData.find(c => {
        const countryName = normalizeCountryName(c.name);
        return countryName.includes(normalizedName) || normalizedName.includes(countryName);
    });
    
    return partialMatch ? partialMatch.code : null;
}

/**
 * Get full country object by code
 */
export function getCountryByCode(code: string): Country | null {
    const normalizedCode = code.toUpperCase().trim();
    return countriesData.find(c => c.code === normalizedCode) || null;
}

/**
 * Get full country object by name
 */
export function getCountryByName(name: string): Country | null {
    const code = getCountryCodeByName(name);
    return code ? getCountryByCode(code) : null;
}

/**
 * Check if two country identifiers (names or codes) represent the same country
 */
export function areCountriesEqual(country1: string, country2: string): boolean {
    if (!country1 || !country2) return false;
    
    // If they're exactly the same, return true
    if (country1.toLowerCase().trim() === country2.toLowerCase().trim()) {
        return true;
    }
    
    // Try to resolve both to codes and compare
    const code1 = getCountryCode(country1);
    const code2 = getCountryCode(country2);
    
    return code1 !== null && code2 !== null && code1 === code2;
}

/**
 * Get country code from either a name or code input
 */
export function getCountryCode(input: string): string | null {
    const trimmed = input.trim();
    
    // If it looks like a code (2 uppercase letters), try to find it directly
    if (/^[A-Z]{2}$/.test(trimmed)) {
        const found = countriesData.find(c => c.code === trimmed);
        return found ? found.code : null;
    }
    
    // Otherwise treat it as a name
    return getCountryCodeByName(input);
}

/**
 * Get country name from either a name or code input
 */
export function getCountryName(input: string): string | null {
    const trimmed = input.trim();
    
    // If it looks like a code (2 uppercase letters), get name by code
    if (/^[A-Z]{2}$/.test(trimmed)) {
        return getCountryNameByCode(trimmed);
    }
    
    // If it's already a valid country name, normalize it
    const code = getCountryCodeByName(input);
    return code ? getCountryNameByCode(code) : null;
}

/**
 * Search for countries by partial name match
 */
export function searchCountries(query: string): Country[] {
    const normalizedQuery = normalizeCountryName(query);
    
    return countriesData.filter(country => {
        const normalizedName = normalizeCountryName(country.name);
        const normalizedCode = country.code.toLowerCase();
        
        // Check name match
        if (normalizedName.includes(normalizedQuery)) return true;
        
        // Check code match
        if (normalizedCode.includes(normalizedQuery)) return true;
        
        // Check variations
        const variations = REVERSE_VARIATIONS[country.name] || [];
        return variations.some(variation => 
            normalizeCountryName(variation).includes(normalizedQuery)
        );
    });
}

/**
 * Get all countries data
 */
export function getAllCountries(): Country[] {
    return [...countriesData];
}

/**
 * Validate if a string is a valid country name or code
 */
export function isValidCountry(input: string): boolean {
    return getCountryCode(input) !== null;
}
